import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { format } from "date-fns";
import { X } from "lucide-react";
import { useLocalStorage } from "usehooks-ts";

import { Button } from "components/ui/Button";
import { Pill } from "components/ui/Pill";
import { useGetCDM } from "features/cdm/cdm/hooks/useGetCDM";
import { CarePlanForm } from "features/cdm/components/CarePlanForm/CarePlanForm";
import { CdmReviewReferences } from "features/cdm/components/CdmReviewReferences";
import { DiscardFormModal } from "features/cdm/components/DiscardFormModal/DiscardFormModal";
import { ReviewAndExamForm } from "features/cdm/components/ReviewAndExamForm/ReviewAndExamForm";
import { Steps } from "features/cdm/components/Steps/Steps";
import {
  getReviewTypeBucket,
  isRegistration,
} from "features/cdm/constants/reviewTypes";
import { useGetCarePlan } from "features/cdm/hooks/useGetCarePlan";
import { useLatestReviewId } from "features/cdm/hooks/useLatestReviewId";
import { views } from "features/cdm/hooks/useView";
import { getCdmField } from "features/cdm/utils/cdmMatrix";
import { writeCdmMorbidityCarryForward } from "features/cdm/utils/cdmMorbidityCarryForward";
import { buildClinicalPrefill } from "features/cdm/utils/clinicalDataPrefill";
import { extractSubmissionError } from "features/cdm/utils/extractSubmissionError";
import { usePatientBaseline } from "features/patients/hooks/usePatientBaseline";
import { usePatientClinicalData } from "hooks/usePatientClinicalData";
import { formatCdmStatus,getCdmStatusColors } from "utils/cdmStatus";

import style from "./CDMItemForm.module.css";
import { PlanAndOutcomeForm } from "./components/PlanAndOutcomeForm/PlanAndOutcomeForm";
import { RiskAndAssessmentForm } from "./components/RiskAndAssessmentForm/RiskAndAssessmentForm";
import { TestAndMedicationForm } from "./components/TestAndMedicationForm/TestAndMedicationForm";
import { useSendCDM } from "./hooks/useSendCDM";
import { useValidateCDM } from "./hooks/useValidateCDM";

const CDM_STEPS = [
  "Review & Exam",
  "Tests & Medications",
  "Risk & Assessment",
  "Vaccinations",
  "Care Plan",
];

export const CDMItemForm = ({
  item,
  setView,
  isAnnualReview,
  patientId,
  removeItem,
  type,
  registrationReasons,
}) => {
  const [draft, setDraft, removeDraft] = useLocalStorage(
    `kinesin-cdm-draft-${patientId}-${item?.id}`,
    { data: item, activeStep: 1 }
  );

  const { data, activeStep } = draft;
  const setData = (updater) =>
    setDraft((prev) => ({
      ...prev,
      data: typeof updater === "function" ? updater(prev.data) : updater,
    }));
  const setActiveStep = (step) =>
    setDraft((prev) => ({ ...prev, activeStep: step }));

  const [isDiscardModalOpen, setIsDiscardModalOpen] = useState(false);

  const { data: baselineData } = usePatientBaseline(patientId);
  const { data: clinicalData } = usePatientClinicalData(patientId);
  // New/edited review has no care plan of its own yet — prefill from the
  // patient's latest existing review (previous goals, current medications).
  const latestReviewId = useLatestReviewId(patientId);
  const { data: carePlanView } = useGetCarePlan(latestReviewId);
  // Carry the previous review's CHA2DS2-VASc factors / QRisk3 conditions forward
  // via the API so they pre-seed the calculators cross-device (KIN-1261).
  // localStorage is only a fallback for reviews that predate the round-trip.
  const { data: latestReviewData } = useGetCDM(latestReviewId);

  const { mutateAsync: validateCDM } = useValidateCDM({ patientId });
  const { mutateAsync: sendCDM, isPending } = useSendCDM({
    patientId,
  });

  useEffect(() => {
    if (!item) {
      setView({ name: views.noChronicDiseases, cdmId: null });
    }
  }, [item, setView]);

  useEffect(() => {
    if (!clinicalData) return;

    const reviewType = getReviewTypeBucket(type);
    const { bloods, vitals, diagnostics, lifestyle } =
      buildClinicalPrefill(clinicalData);

    setData((prev) => {
      const merged = {};

      // Bloods are gated per condition × review by the matrix.
      for (const [field, value] of Object.entries(bloods)) {
        if (
          !prev[field] &&
          getCdmField(registrationReasons, field, reviewType)
        ) {
          merged[field] = value;
        }
      }

      // Vitals / diagnostics / lifestyle render on fixed steps, so they are not
      // matrix-gated — prefill whatever the endpoint returned (a user edit,
      // already in `prev`, always wins).
      for (const group of [vitals, diagnostics, lifestyle]) {
        for (const [field, value] of Object.entries(group)) {
          if (!prev[field]) merged[field] = value;
        }
      }

      return Object.keys(merged).length > 0 ? { ...prev, ...merged } : prev;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clinicalData]);

  if (!item) {
    return null;
  }

  const reviewType = getReviewTypeBucket(type);

  return (
    <section className={style.wrapper}>
      <div className={style.header}>
        <div className={style.headerInner}>
          <h3>{item.name}</h3>
          <p>{format(new Date(item.date), "dd-MM-yyyy")}</p>
        </div>
        <div className={style.statusWrapper}>
          <Pill
            color={getCdmStatusColors(item.status).color}
            bg={getCdmStatusColors(item.status).bg}
            border={getCdmStatusColors(item.status).border}
          >
            {formatCdmStatus(item.status)}
          </Pill>
          <Button
            variant="outlineGray"
            iconButton
            onClick={() => setIsDiscardModalOpen(true)}
            aria-label="Discard form"
          >
            <X size={16} />
          </Button>
        </div>
      </div>
      {isDiscardModalOpen && (
        <DiscardFormModal
          onClose={() => setIsDiscardModalOpen(false)}
          onConfirm={() => {
            removeDraft();
            removeItem();
          }}
        />
      )}
      <Steps steps={CDM_STEPS} activeStep={activeStep} />
      <CdmReviewReferences patientId={patientId} />
      {activeStep === 1 && (
        <ReviewAndExamForm
          data={data}
          baselineData={baselineData}
          onContinue={(newData) => {
            setData((prev) => ({ ...prev, ...newData }));
            setActiveStep(activeStep + 1);
          }}
        />
      )}
      {activeStep === 3 && (
        <RiskAndAssessmentForm
          data={data}
          patientId={patientId}
          carriedFactorsFromApi={{
            cha2ds2vascScore: latestReviewData?.cha2ds2vascScore,
            cha2ds2vascFactors: latestReviewData?.cha2ds2vascFactors,
            qRisk3Conditions: latestReviewData?.qRisk3Conditions,
          }}
          onBack={() => setActiveStep(activeStep - 1)}
          onContinue={(newData) => {
            setData((prev) => ({ ...prev, ...newData }));
            setActiveStep(activeStep + 1);
          }}
          type={reviewType}
          registrationReasons={registrationReasons}
        />
      )}
      {activeStep === 2 && (
        <TestAndMedicationForm
          data={data}
          onBack={(newData) => {
            setData((prev) => ({ ...prev, ...newData }));
            setActiveStep(activeStep - 1);
          }}
          onContinue={async (newData) => {
            setData((prev) => ({ ...prev, ...newData }));
            setActiveStep(activeStep + 1);
          }}
          medications={item.medications}
          isAnnualReview={isAnnualReview}
          type={reviewType}
          registrationReasons={registrationReasons}
        />
      )}
      {activeStep === 4 && (
        <PlanAndOutcomeForm
          data={data}
          onBack={() => setActiveStep(activeStep - 1)}
          type={reviewType}
          registrationReasons={registrationReasons}
          onContinue={(newData) => {
            setData((prev) => ({ ...prev, ...newData }));
            setActiveStep(activeStep + 1);
          }}
        />
      )}
      {activeStep === 5 && (
        <CarePlanForm
          data={data}
          carePlanView={carePlanView}
          isRegistration={isRegistration(item?.reviewType)}
          isLoading={isPending}
          onBack={() => setActiveStep(activeStep - 1)}
          onContinue={async (newData) => {
            const dataToSend = { ...data, ...newData };

            const validateRes = await validateCDM(dataToSend);

            if (!validateRes?.isValid) {
              toast.error(
                validateRes.validationErrors?.length > 0
                  ? validateRes.validationErrors.join(", ")
                  : "Something went wrong"
              );

              return;
            }

            try {
              const result = await sendCDM(dataToSend);
              // Fallback snapshot for carry-forward (KIN-1261). The API is now
              // the source of truth (factors round-tripped on the review DTO);
              // this keeps carry-forward working offline / before the next GET.
              writeCdmMorbidityCarryForward(patientId, {
                cha2ds2vascScore: dataToSend.cha2ds2vascScore,
                cha2ds2vascFactors: dataToSend.cha2ds2vascFactors,
                qRisk3Conditions: dataToSend.qRisk3Conditions,
              });
              // KIN-1236: a mid-month / panel-transfer review is held by the BE
              // as a draft (HL7 not sent) until PCRS can verify the panel on the
              // 1st. Communicate the hold instead of "sent".
              if (result?.held) {
                const releaseDate = result.holdUntil
                  ? format(new Date(result.holdUntil), "d MMMM yyyy")
                  : "the 1st of next month";
                toast.success(
                  `Saved as a draft — held for PCRS panel verification. It will be submitted on ${releaseDate}.`
                );
              } else {
                toast.success("CDM sent successfully");
              }
              removeDraft();
              removeItem();
            } catch (error) {
              toast.error(
                extractSubmissionError(error, "Error while sending CDM")
              );
            }
          }}
        />
      )}
    </section>
  );
};
